import { useState, useEffect, useRef, useCallback } from 'react';
import { SOCKET_EVENTS } from '@maios/shared';
import { getSocket } from '../lib/socket';
import { useAuthStore } from '../store/authStore';

interface TypingUser {
  userId: string;
  userName: string;
}

export function useTypingIndicator(channelId: string, options?: { threadId?: string }) {
  const threadId = options?.threadId;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const lastTypingRef = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;
    const now = Date.now();
    if (now - lastTypingRef.current > 3000) {
      socket.emit(SOCKET_EVENTS.CHANNEL_TYPING_START, { channelId, threadId });
      lastTypingRef.current = now;
    }
    // Auto-stop after 3s inactivity
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(() => {
      socket.emit(SOCKET_EVENTS.CHANNEL_TYPING_STOP, { channelId, threadId });
      lastTypingRef.current = 0;
    }, 3000);
  }, [channelId, threadId]);

  const stopTyping = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;
    socket.emit(SOCKET_EVENTS.CHANNEL_TYPING_STOP, { channelId, threadId });
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    lastTypingRef.current = 0;
  }, [channelId, threadId]);

  // Listen for typing events
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handler = (data: { channelId: string; userId: string; userName: string; isTyping: boolean; threadId?: string }) => {
      if (data.channelId !== channelId) return;
      if (data.userId === currentUserId) return;
      // Filter by threadId: thread typing only shown in thread panel, main typing only in main
      if (threadId) {
        if (data.threadId !== threadId) return;
      } else {
        if (data.threadId) return;
      }

      setTypingUsers((prev) => {
        if (data.isTyping) {
          if (prev.some((u) => u.userId === data.userId)) return prev;
          return [...prev, { userId: data.userId, userName: data.userName }];
        }
        return prev.filter((u) => u.userId !== data.userId);
      });
    };

    socket.on(SOCKET_EVENTS.CHANNEL_TYPING, handler);
    return () => {
      socket.off(SOCKET_EVENTS.CHANNEL_TYPING, handler);
    };
  }, [channelId, currentUserId, threadId]);

  // Reset on channel/thread change
  useEffect(() => {
    setTypingUsers([]);
    return () => {
      const socket = getSocket();
      if (socket) socket.emit(SOCKET_EVENTS.CHANNEL_TYPING_STOP, { channelId, threadId });
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, [channelId, threadId]);

  return { typingUsers, handleTyping, stopTyping };
}
